import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { OrganizationList } from '../components/organization/OrganizationList'
import { OrganizationForm } from '../components/organization/OrganizationForm'
import { DepartmentList } from '../components/organization/DepartmentList'
import { DepartmentForm } from '../components/organization/DepartmentForm'
import { OrganizationMemories } from '../components/organization/OrganizationMemories'
import type { Organization, Department } from '../types'
import { Button } from '../components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'

type View = 'list' | 'create-org' | 'edit-org' | 'view-org' | 'create-dept' | 'edit-dept'

export function OrganizationsPage() {
  const [view, setView] = useState<View>('list')
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [selectedDept, setSelectedDept] = useState<Department | null>(null)

  const handleOrgCreated = (org: Organization) => {
    setSelectedOrg(org)
    setView('view-org')
  }

  const handleOrgUpdated = (org: Organization) => {
    setSelectedOrg(org)
    setView('view-org')
  }

  const handleDeptCreated = () => {
    setView('view-org')
    setSelectedDept(null)
  }

  const handleDeptUpdated = () => {
    setView('view-org')
    setSelectedDept(null)
  }

  const handleBack = () => {
    if (view === 'view-org' || view === 'edit-org') {
      setView('list')
      setSelectedOrg(null)
    } else if (view === 'create-dept' || view === 'edit-dept') {
      setView('view-org')
      setSelectedDept(null)
    } else {
      setView('list')
    }
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header with back button */}
      {view !== 'list' && (
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      )}

      {/* List View */}
      {view === 'list' && (
        <OrganizationList
          onSelect={(org) => {
            setSelectedOrg(org)
            setView('view-org')
          }}
          onEdit={(org) => {
            setSelectedOrg(org)
            setView('edit-org')
          }}
          onCreate={() => setView('create-org')}
        />
      )}

      {/* Create Organization */}
      {view === 'create-org' && (
        <div className="max-w-2xl mx-auto">
          <OrganizationForm
            onSuccess={handleOrgCreated}
            onCancel={() => setView('list')}
          />
        </div>
      )}

      {/* Edit Organization */}
      {view === 'edit-org' && selectedOrg && (
        <div className="max-w-2xl mx-auto">
          <OrganizationForm
            organization={selectedOrg}
            onSuccess={handleOrgUpdated}
            onCancel={handleBack}
          />
        </div>
      )}

      {/* View Organization with Departments */}
      {view === 'view-org' && selectedOrg && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">{selectedOrg.name}</h1>
                {selectedOrg.industry && (
                  <p className="text-lg text-muted-foreground mb-2">{selectedOrg.industry}</p>
                )}
                {selectedOrg.description && (
                  <p className="text-muted-foreground">{selectedOrg.description}</p>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => setView('edit-org')}
              >
                Edit Organization
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              {selectedOrg.size && (
                <div>
                  <p className="text-sm text-muted-foreground">Size</p>
                  <p className="font-medium capitalize">{selectedOrg.size}</p>
                </div>
              )}
              {selectedOrg.website && (
                <div>
                  <p className="text-sm text-muted-foreground">Website</p>
                  <a
                    href={selectedOrg.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Visit
                  </a>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-medium">
                  {new Date(selectedOrg.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium">{selectedOrg.is_active ? 'Active' : 'Inactive'}</p>
              </div>
            </div>
          </div>

          <Tabs defaultValue="departments" className="w-full">
            <TabsList>
              <TabsTrigger value="departments">Departments</TabsTrigger>
              <TabsTrigger value="memories">Memories</TabsTrigger>
            </TabsList>
            <TabsContent value="departments" className="mt-6">
              <DepartmentList
                organizationId={selectedOrg.id}
                onEdit={(dept) => {
                  setSelectedDept(dept)
                  setView('edit-dept')
                }}
                onCreate={() => setView('create-dept')}
              />
            </TabsContent>
            <TabsContent value="memories" className="mt-6">
              <OrganizationMemories organizationId={selectedOrg.id} />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Create Department */}
      {view === 'create-dept' && selectedOrg && (
        <div className="max-w-2xl mx-auto">
          <DepartmentForm
            organizationId={selectedOrg.id}
            onSuccess={handleDeptCreated}
            onCancel={handleBack}
          />
        </div>
      )}

      {/* Edit Department */}
      {view === 'edit-dept' && selectedOrg && selectedDept && (
        <div className="max-w-2xl mx-auto">
          <DepartmentForm
            organizationId={selectedOrg.id}
            department={selectedDept}
            onSuccess={handleDeptUpdated}
            onCancel={handleBack}
          />
        </div>
      )}
    </div>
  )
}
